import React from "react";

const PrintableEnvironmentalAssessment = ({ assessment, questions = [] }) => {
  if (!assessment) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (e) {
      return "";
    }
  };

  const answeredQuestions = new Map(
    assessment.answers.map((answer) => [answer.question_id, answer])
  );

  const allQuestionsWithAnswers = questions.map((question, index) => {
    const answer = answeredQuestions.get(question.id);
    return {
      ...question,
      questionNumber: index + 1,
      answer: answer?.answer || "",
      note: answer?.note || "",
      isAnswered: Boolean(answer),
    };
  });

  const totalQuestions = questions.length;

  const printStyles = `
    @media print {
      body {
        font-family: Arial, sans-serif;
        margin: 0;
        padding: 0;
        color: #000;
        background-color: #fff;
      }

      .print-container {
        width: 100%;
        max-width: 750px;
        margin: 0 auto;
        padding: 20px;
        box-sizing: border-box;
      }

      h1, h2 {
        font-weight: bold;
        color: #000;
        margin-top: 15px;
        margin-bottom: 10px;
      }

      h1 {
        font-size: 24pt;
        text-align: center;
        margin-bottom: 25px;
        padding-bottom: 8px;
        border-bottom: 1px solid #000;
      }

      h2 {
        font-size: 14pt;
        border-bottom: 1px dashed #ccc;
        padding-bottom: 5px;
        margin-bottom: 15px;
      }

      p {
        font-size: 10pt;
        line-height: 1.4;
        margin-bottom: 5px;
      }

      .info-group {
        display: flex;
        flex-wrap: wrap;
        gap: 20px;
        margin-bottom: 20px;
      }

      .info-column {
        flex: 1 1 45%;
      }

      .info-pair {
        display: flex;
        margin-bottom: 4px;
        font-size: 9pt;
      }

      .info-pair strong {
        min-width: 110px;
        font-weight: 600;
        color: #333;
        flex-shrink: 0;
      }

      .info-pair span {
        flex-grow: 1;
      }

      .section-divider {
        border-top: 1px dashed #ccc;
        margin: 20px 0;
      }

      .signature-box {
        border: 1px solid #444;
        padding: 4px;
        width: 120px;
        height: 50px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #f9f9f9;
        margin-top: 5px;
      }

      .signature-box img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 20px;
        table-layout: fixed;
      }

      th, td {
        border: 1px solid #000;
        padding: 8px;
        text-align: left;
        vertical-align: top;
      }

      th {
        background-color: #eee;
        font-weight: bold;
        font-size: 9pt;
      }

      td {
        font-size: 9pt;
      }

      .question-number {
        width: 30px;
      }

      .answer-cell {
        text-align: center;
        width: 50px;
      }

      .notes-cell {
        width: 150px;
      }

      .answer-text {
        font-weight: bold;
      }

      .action-required-section {
        border: 1px dashed #000;
        padding: 15px;
        margin-bottom: 20px;
      }

      .report-footer {
        margin-top: 30px;
        padding-top: 15px;
        border-top: 1px solid #ccc;
        font-size: 9pt;
        color: #444;
        display: flex;
        flex-wrap: wrap;
        gap: 20px;
        justify-content: space-between;
      }

      .footer-column {
        flex: 1 1 45%;
      }

      table {
        page-break-inside: auto;
      }
      tr {
        page-break-inside: avoid;
        page-break-after: auto;
      }
      thead {
        display: table-header-group;
      }
    }
  `;

  return (
    <div className="print-container">
      <style>{printStyles}</style>

      <h1>Environmental Risk Assessment Report</h1>

      <h2>Assessment Information</h2>
      <div className="info-group">
        <div className="info-column">
          <div className="info-pair">
            <strong>Client Name:</strong> <span>{assessment.client_name}</span>
          </div>
          <div className="info-pair">
            <strong>Assessor Name:</strong>{" "}
            <span>{assessment.assessor_name}</span>
          </div>
          <div className="info-pair">
            <strong>Score:</strong> <span>{assessment.score}</span>
          </div>
        </div>
        <div className="info-column">
          <div className="info-pair">
            <strong>Risk Level:</strong> <span>{assessment.risk_level}</span>
          </div>
          <div className="info-pair">
            <strong>Date Completed:</strong>{" "}
            <span>{formatDate(assessment.date_completed)}</span>
          </div>
          <div className="info-pair">
            <strong>Next Review Date:</strong>{" "}
            <span>{formatDate(assessment.next_review_date)}</span>
          </div>
        </div>
      </div>

      <div className="section-divider"></div>

      <h2>Assessment Questions ({totalQuestions} Total)</h2>
      <table>
        <thead>
          <tr>
            <th className="question-number">No.</th>
            <th>Question</th>
            <th className="answer-cell">Answer</th>
            <th className="notes-cell">Notes</th>
          </tr>
        </thead>
        <tbody>
          {allQuestionsWithAnswers.map((question) => (
            <tr key={question.id}>
              <td className="question-number">{question.questionNumber}</td>
              <td>{question.question_text}</td>
              <td className="answer-cell">
                <span className="answer-text">{question.answer}</span>
              </td>
              <td>
                <span>{question.note}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {assessment.action_required && (
        <>
          <div className="section-divider"></div>
          <div className="action-required-section">
            <h2>Action Required</h2>
            <p>{assessment.action_required}</p>
          </div>
        </>
      )}

      <div className="report-footer">
        <div className="footer-column">
          <div className="info-pair">
            <strong>Assessment Address:</strong>
            <span>{assessment.address || "N/A"}</span>
          </div>
          <div className="info-pair">
            <strong>Report Generated On:</strong>
            <span>{formatDate(new Date())}</span>
          </div>
        </div>
        <div className="footer-column">
          <div className="info-pair">
            <strong>Assessor Signature:</strong>
          </div>
          {assessment?.assessor_signature ? (
            <div className="signature-box">
              <img
                src={assessment.assessor_signature}
                alt="Assessor Signature"
              />
            </div>
          ) : (
            <span style={{ fontStyle: "italic" }}>No signature</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrintableEnvironmentalAssessment;
